import { useState, useEffect } from 'react';
import {
    useNavigate,
    useLocation,
} from 'react-router-dom';
import { useSnackbar } from "notistack";

import { IconChevronLeft, IconEdit, IconDeviceFloppy, IconCancel, IconTrash, IconDatabasePlus, IconRefresh } from '@tabler/icons-react';

import { useCompanyHook } from '../../Hooks/Companies/companyHooks';

import { CompanyDetailsInterface, Company } from '../../Models/Company/Company';
import { Authentication } from "../../Models/Authentication/Authentication";

import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import RequiredFieldHelper from '../../Utilities/Helpers/RequiredFieldHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { CompanyField, CompanyAddressField, CompanyLicenseField, AuthenticationField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import { ErrorMessage } from '../../Constants/Messages';

import FormTextField from '../../CustomViews/Elements/FormTextField';
import AutoCompleteDropdown from '../../CustomViews/Elements/AutoCompleteDropdown';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import VendorsDropdown from '../../CustomViews/Elements/AutoComplete/VendorsDropdown';
import CustomCheckbox from '../../CustomViews/Elements/CustomCheckbox';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import CustomTooltip from '../../CustomViews/Elements/CustomTooltip';

import { Card, Typography } from '@mui/material';
import styled from 'styled-components';

const FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 30px;

    .databaseItemContainer {
        .dbConnectionStatusContainer {
            line-height: 3;
            button {
                margin: 0px 10px 0px 0px
            }
        }

        .dbValue {
            display: flex;
            align-items: center;

            .dbConnectionStatus {
                width: 15px;
                height: 15px;
                border-radius: 15px;
                margin: 0px 0px 0px 10px;
            }

            .connectedStatus {
                background: var(--fieldSwitchedCheckedBGSuccessColor);
            }

            .notConnectedStatus {
                background: var(--fieldSwitchedCheckedBGDangerColor);
            }
        }
    }
`;

const FieldItemCard = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
`;

const FieldItemName = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    color: #878a99 !important;
    font-weight: 600 !important;
    font-size: 0.80rem !important;
    margin: 0px 0px 5px 0px !important;
`;

const FieldItemValue = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    font-weight: 500 !important;
    font-size: 16px !important;
`;

const FormFieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    .checkBoxContainer {
        .formControlContainer {
            .formCheckboxContainer {
                .checkboxSideLabel {
                    font-size: 12px;
                    font-weight: 500;
                    // color: ${(props) => props.$darkMode ? '#ced4da' : '#495057'} !important;
                    color: #6179b3 !important;
					word-break: break-all;
                }
                
                input {
                    width: 20px;
                    height: 20px;

                    &:before {
                        width: 7px;
                        height: 14px;
                        margin: 1px 6px;
                    }
                }
            }
        }

    }
`;

const Form2FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
`;

export default function CompanyDetails(props: CompanyDetailsInterface) {
    //Variables
    let { companyid, viewType, setviewType, setopenDelete, setdeleteType, settoDeleteItem, previewData, formData, setformData, errorMessages, setErrorMessages, DropdownData, setreinitializeData, getCompanyData } = props;
    let requiredFields = RequiredParametersHelper.company();
    let navigate = useNavigate();
    let location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    let userData: Authentication = LocalStorageHelper.getUserData();

    //Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        id: companyid,
    });

    const q_queryKey = [CachingEnum.COMPANY_USERS, queryStr];
    const { createCompany, editCompanyById, generateCompanyDatabase } = useCompanyHook(q_queryKey, queryStr, false);

    const comNameProps = {
        name: CompanyField.NAME,
        title: 'Company Name',
        fieldValue: formData?.[CompanyField.NAME] ?? "",
        type: 'text',
        required: requiredFields?.find((field: any) => field.keyName == CompanyField.NAME)?.keyName != null,
        disabled: viewType != ViewType.ADD,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            if(setformData) {
                setformData(prev => ({ ...prev, [CompanyField.NAME]: selectedValue }));
            }
        },
    },
        billingAddressProps = {
            name: CompanyAddressField.STREET_ADDRESS,
            title: 'Billing Address',
            fieldValue: formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.STREET_ADDRESS] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.ADDRESS)?.fields?.find((f: any) => f.keyName == CompanyAddressField.STREET_ADDRESS)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [CompanyField.ADDRESS]: {...formData?.[CompanyField.ADDRESS], [CompanyAddressField.STREET_ADDRESS]: selectedValue }};

                updateAddress(_formData);
            },
        },
        contPersonProps = {
            name: CompanyField.CONTACT_PERSON,
            title: 'Contact Person',
            fieldValue: formData?.[CompanyField.CONTACT_PERSON] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.CONTACT_PERSON)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.CONTACT_PERSON]: selectedValue }));
                }
            },
        },
        contPhoneProps = {
            name: CompanyField.CONTACT_PHONE,
            title: 'Phone Number',
            fieldValue: formData?.[CompanyField.CONTACT_PHONE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.CONTACT_PHONE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.CONTACT_PHONE]: selectedValue }));
                }
            },
        },
        cityProps = {
            name: CompanyAddressField.CITY,
            title: 'City',
            fieldValue: formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.CITY] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyAddressField.CITY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [CompanyField.ADDRESS]: {...formData?.[CompanyField.ADDRESS], [CompanyAddressField.CITY]: selectedValue }};

                updateAddress(_formData);
            },
        },
        stateProps = {
            name: CompanyAddressField.STATE,
            title: 'State',
            fieldValue: formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.STATE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyAddressField.STATE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [CompanyField.ADDRESS]: {...formData?.[CompanyField.ADDRESS], [CompanyAddressField.STATE]: selectedValue }};

                updateAddress(_formData);
            },
        },
        contEmailAddressProps = {
            name: CompanyField.EMAIL,
            title: 'Email Address',
            fieldValue: formData?.[CompanyField.EMAIL] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.EMAIL)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.EMAIL]: selectedValue }));
                }
            },
        },
        companyDatabaseProps = {
            name: CompanyField.DATABASE,
            title: 'Database',
            fieldValue: formData?.[CompanyField.DATABASE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.DATABASE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.DATABASE]: selectedValue }));
                }
            },
        },
        postalCodeProps = {
            name: CompanyAddressField.ZIP_CODE,
            title: 'Postal Code',
            fieldValue: formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.ZIP_CODE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyAddressField.ZIP_CODE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [CompanyField.ADDRESS]: {...formData?.[CompanyField.ADDRESS], [CompanyAddressField.ZIP_CODE]: selectedValue }};

                updateAddress(_formData);
            },
        },
        countryProps = {
            name: CompanyAddressField.COUNTRY,
            title: 'Country',
            fieldValue: formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.COUNTRY] ?? "",
            options: DropdownData[CachingEnum.COUNTRY_LIST] ?? [],
            isLoadingDropdown: false,
            required: requiredFields?.find((field: any) => field.keyName == CompanyAddressField.COUNTRY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [CompanyField.ADDRESS]: {...formData?.[CompanyField.ADDRESS], [CompanyAddressField.COUNTRY]: selectedValue?.id ?? '' }};

                updateAddress(_formData);
            },
        },
        registrationCodeProps = {
            name: CompanyField.REGISTRATION_CODE,
            title: 'Registration Code',
            fieldValue: formData?.[CompanyField.REGISTRATION_CODE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.REGISTRATION_CODE)?.keyName != null,
            disabled: true,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.REGISTRATION_CODE]: selectedValue }));
                }
            },
        },
        vendorProps = {
            name: CompanyField.VENDOR_PUBLIC_ID,
            title: 'Vendor',
            fieldValue: formData?.[CompanyField.VENDOR_PUBLIC_ID] ?? null,
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.VENDOR_PUBLIC_ID)?.keyName != null,
            errorMessages: errorMessages,
            isUserVendorsOnly: userData?.[AuthenticationField.ROLE_TYPE] != RoleTypeEnum.SUPER_USER,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({
                        ...prev,
                        [CompanyField.VENDOR_PUBLIC_ID]: selectedValue?.id ?? null,
                        [CompanyField.VENDOR]: selectedValue?.id ? {
                            publicId: selectedValue?.id ?? null,
                            name: selectedValue?.name ?? null
                        } : null,
                    }));
                }
            },
        },
        vendorRegistrationCodeProps = {
            name: CompanyField.VENDOR_REGISTRATION_CODE,
            title: 'Vendor Registration Code',
            fieldValue: formData?.[CompanyField.VENDOR_REGISTRATION_CODE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.VENDOR_REGISTRATION_CODE)?.keyName != null,
            disabled: true,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.VENDOR_REGISTRATION_CODE]: selectedValue }));
                }
            },
        },
        isOnPremiseProps = {
            name: CompanyField.IS_ON_PREMISE,
            title: 'Is On Premise',
            fieldValue: formData?.[CompanyField.IS_ON_PREMISE] ?? false,
            checkboxType: ButtonType.PRIMARY,
            required: requiredFields?.find((field: any) => field.keyName == CompanyField.IS_ON_PREMISE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData(prev => ({ ...prev, [CompanyField.IS_ON_PREMISE]: selectedValue }));
                }
            },
        };

    //Event Handlers
    const handleSubmit = async () => {
        let _validateForm = RequiredFieldHelper.validateForm(requiredFields, formData, errorMessages, setErrorMessages, enqueueSnackbar);

        if (_validateForm?.hasError) { return true; }

        let _success: boolean = false;
        let publicId = "0";
        let proceedReinitialized = false;

        if(!formData?.[CompanyField.PUBLIC_ID]) {
            let createResponse = await createCompany(JSON.stringify(formData));

            _success = createResponse?.isSuccess ?? false;
            publicId = createResponse?.data?.data?.[CompanyField.PUBLIC_ID] ?? '0';
            proceedReinitialized = true;
        } else {
            let editResponse = await editCompanyById(formData?.[CompanyField.PUBLIC_ID], JSON.stringify(formData));

            _success = editResponse?.isSuccess ?? false;
            publicId = editResponse?.data?.data?.[CompanyField.PUBLIC_ID] ?? '0';
        }

        if(_success) {
            navigate(`${RouteEnum.COMPANY_DETAILS}?id=${publicId}&view=${ViewType.PREVIEW}`, { replace: true });

            if(formData?.[CompanyField.PUBLIC_ID] && getCompanyData) {
                getCompanyData();
            }

            if(setviewType) {
                setviewType(ViewType.PREVIEW);
            }

            if(setreinitializeData && proceedReinitialized) {
                setreinitializeData(true);
            }
        }
    }

    const handleGenerateCompanyDatabase = async () => {
        let _success: boolean = false;

        if(formData?.[CompanyField.PUBLIC_ID]) {
            let generateResponse = await generateCompanyDatabase(formData?.[CompanyField.PUBLIC_ID]);

            _success = generateResponse?.isSuccess ?? false;
        }

        if(_success && formData?.[CompanyField.PUBLIC_ID] && getCompanyData) {
            getCompanyData();
        }
    }

    const updateAddress = async (_formData: any) => {
        let _streetAddress = _formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.STREET_ADDRESS] ?? "";
        let _city = _formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.CITY] ?? "";
        let _state = _formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.STATE] ?? "";
        let _zipCode = _formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.ZIP_CODE] ?? "";
        let _country = DropdownData[CachingEnum.COUNTRY_LIST]?.find((x: any) => x.id == (_formData?.[CompanyField.ADDRESS]?.[CompanyAddressField.COUNTRY] ?? 0))?.name ?? "";
        let _fullAddress = _streetAddress ?? "";

        _fullAddress += _city ? " " + _city : "";
        _fullAddress += _state ? " " + _state : "";
        _fullAddress += _zipCode ? " " + _zipCode : "";
        _fullAddress += _country ? " " + _country : "";

        if(setformData) {
            setformData({..._formData, [CompanyField.ADDRESS]: {..._formData?.[CompanyField.ADDRESS], [CompanyAddressField.FULL_ADDRESS]: _fullAddress}});
        }
    }

    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.COMPANIES);
                }} />
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.COMPANY_DELETE) && <CustomButton
                btnType={ButtonType.DELETE}
                title='Delete'
                startIcon={<IconTrash />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    if (setopenDelete) {
                        setopenDelete(true);
                    }
                    
                    if (setdeleteType) {
                        setdeleteType('company');
                    }

                    if(settoDeleteItem) {
                        settoDeleteItem(previewData);
                    }
                }} />}
            {viewType == ViewType.EDIT && <CustomButton
                btnType={ButtonType.CANCEL}
                title='Cancel'
                startIcon={<IconCancel />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    navigate(`${RouteEnum.COMPANY_DETAILS}?id=${companyid}&view=${ViewType.PREVIEW}`, { replace: true });

                    if(setviewType) {
                        setviewType(ViewType.PREVIEW);
                    }
                }} />}
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.COMPANY_EDIT) && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Edit'
                startIcon={<IconEdit />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    navigate(`${RouteEnum.COMPANY_DETAILS}?id=${companyid}&view=${ViewType.EDIT}`, { replace: true });
                    if(setviewType) {
                        setviewType(ViewType.EDIT);
                    }
                }} />}
            {(viewType == ViewType.EDIT || viewType == ViewType.ADD) && <CustomButton
                btnType={ButtonType.SUCCESS}
                title='Save'
                startIcon={<IconDeviceFloppy />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    handleSubmit();
                }} />}
        </>
    }

    const renderPreviewData = () => {
        return <FieldItemsContainer>
            <FieldItemCard>
                <FieldItemName>{comNameProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.NAME] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{contPersonProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.CONTACT_PERSON] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>Created Date</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.CREATED_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{contEmailAddressProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.EMAIL] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{contPhoneProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.CONTACT_PHONE] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>Last Updated</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.UPDATED_DATE] ? DateHelper.getFormattedDate(previewData?.[CompanyField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{billingAddressProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.ADDRESS]?.[CompanyAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard className='databaseItemContainer'>
                <FieldItemName>{companyDatabaseProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue className='dbValue'>
                    {previewData?.[CompanyField.DATABASE] ?? "--"}
                    <CustomTooltip {...{
                        tooltipName: previewData?.[CompanyField.IS_DATABASE_EXIST] ? 'Connected' : 'Disconnected',
                        placement: "top",
                        tooltipContent: <div className={'dbConnectionStatus ' + (previewData?.[CompanyField.IS_DATABASE_EXIST] ? 'connectedStatus' : 'notConnectedStatus')} />,
                    }} />
                </FieldItemValue>

                <div className='dbConnectionStatusContainer'>
                    <CustomButton
                        btnType={previewData?.[CompanyField.IS_DATABASE_EXIST] ? ButtonType.CANCEL : ButtonType.PRIMARY_LIGHT}
                        disabled={previewData?.[CompanyField.IS_DATABASE_EXIST] ?? false}
                        title='Generate'
                        startIcon={<IconDatabasePlus />}
                        onClick={(e: any) => {
                            if(previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY]) {
                                handleGenerateCompanyDatabase();
                            } else {
                                enqueueSnackbar(ErrorMessage.NO_LICENSE_KEY_DETECTED, { variant: 'error' });
                            }
                        }} />

                    <CustomButton
                        btnType={ButtonType.PRIMARY_LIGHT}
                        title='Refresh'
                        startIcon={<IconRefresh />}
                        onClick={(e: any) => {
                            if(getCompanyData) {
                                getCompanyData();
                            }
                        }} />
                    {/* <CustomIconButton toolTipName='Generate Database'
                        icon={<IconDatabasePlus color="#0288d1" />}
                        disabled={previewData?.[CompanyField.IS_DATABASE_EXIST] ?? false}
                        onClick={(e: any) => {
                            if(previewData?.[CompanyField.LICENSE]?.[CompanyLicenseField.LICENSE_KEY]) {
                                handleGenerateCompanyDatabase();
                            } else {
                                enqueueSnackbar(ErrorMessage.NO_LICENSE_KEY_DETECTED, { variant: 'error' });
                            }
                        }} />

                    <CustomIconButton toolTipName='Refresh'
                        icon={<IconRefresh color="#0288d1" />}
                        onClick={(e: any) => {
                            if(getCompanyData) {
                                getCompanyData();
                            }
                        }} /> */}
                </div>

                
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{registrationCodeProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.REGISTRATION_CODE] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{vendorProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.VENDOR]?.name ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{vendorRegistrationCodeProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.VENDOR_REGISTRATION_CODE] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{isOnPremiseProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[CompanyField.IS_ON_PREMISE] ? "Yes" : "No"}</FieldItemValue>
            </FieldItemCard>

        </FieldItemsContainer>
    }

    const renderDetailsForm = () => {
        return <FormFieldItemsContainer>
            <FormTextField {...comNameProps} />
            <FormTextField {...billingAddressProps} />

            <FormFieldItemsContainer>
                <FormTextField {...contPersonProps} />
                <FormTextField {...contPhoneProps} />
            </FormFieldItemsContainer>
            <FormFieldItemsContainer>
                <FormTextField {...cityProps} />
                <FormTextField {...stateProps} />
            </FormFieldItemsContainer>

            <FormFieldItemsContainer>
                <FormTextField {...contEmailAddressProps} />
                <FormTextField {...companyDatabaseProps} />
            </FormFieldItemsContainer>
            <FormFieldItemsContainer>
                <FormTextField {...postalCodeProps} />
                <AutoCompleteDropdown {...countryProps} />
            </FormFieldItemsContainer>

            
            <VendorsDropdown {...vendorProps} />
            <FormTextField {...registrationCodeProps} />

            <FormTextField {...vendorRegistrationCodeProps} />
            <CustomCheckbox {...isOnPremiseProps} />
        </FormFieldItemsContainer>
    }

    return <CustomDetailsCard
        title={RouteHelper.getRouteDescription(RouteEnum.COMPANY_DETAILS)}
        actionButtons={renderActionButtons()} >
            {viewType == ViewType.PREVIEW && renderPreviewData()}
            {viewType != ViewType.PREVIEW && renderDetailsForm()}
        </CustomDetailsCard>
}
